import type { Skill } from '../skillsData';

export const aeonSuiteSkills: Skill[] = [
  {
    id: 'aeon-distribute-tokens',
    name: 'aeon-distribute-tokens',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'defi',
    description: 'Batch token payouts via the Bankr Wallet API with per-recipient idempotency, two-phase resolve→execute, dry-run preview, and recovery from partial runs.',
    downloads: 3200,
    stars: 150,
    version: '1.0.0',
    installCommand: '> install the aeon-distribute-tokens skill from https://github.com/BankrBot/skills/tree/main/aeon-distribute-tokens',
    usageExample: '> batch token payouts\n> preview dry-run distribution\n> recover partial run',
    detailedDescription: 'Batch token payout orchestration via Bankr Wallet API. Features per-recipient idempotency, two-phase resolve→execute flow, dry-run preview for validation, and automatic recovery mechanisms from partial runs.',
  },
  {
    id: 'aeon-token-movers',
    name: 'aeon-token-movers',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'defi',
    description: 'Daily top movers, losers, and trending coins from CoinGecko with signal enrichment and pump-risk flags.',
    downloads: 4100,
    stars: 190,
    version: '1.0.1',
    installCommand: '> install the aeon-token-movers skill from https://github.com/BankrBot/skills/tree/main/aeon-token-movers',
    usageExample: '> get daily top movers\n> check pump-risk flags\n> analyze trending coins',
    detailedDescription: 'Daily market intelligence on top movers, losers, and trending coins sourced from CoinGecko. Signal enrichment adds context while pump-risk flags warn of potential manipulation.',
  },
  {
    id: 'aeon-monitor-runners',
    name: 'aeon-monitor-runners',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Top 5 tokens that ran hardest in the past 24h across major chains via GeckoTerminal, with pump-risk filters.',
    downloads: 2800,
    stars: 140,
    version: '1.0.0',
    installCommand: '> install the aeon-monitor-runners skill from https://github.com/BankrBot/skills/tree/main/aeon-monitor-runners',
    usageExample: '> find top 24h runners\n> filter pump-risk tokens\n> track across major chains',
    detailedDescription: 'Identify the top 5 tokens with the highest 24-hour performance across major chains via GeckoTerminal data. Built-in pump-risk filters help distinguish genuine momentum from manipulation.',
  },
  {
    id: 'aeon-token-pick',
    name: 'aeon-token-pick',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'defi',
    description: 'One token and one prediction-market pick per run, scored on a quantified rubric with falsifiable thesis, sizing guidance, and kill criterion.',
    downloads: 3500,
    stars: 170,
    version: '1.0.0',
    installCommand: '> install the aeon-token-pick skill from https://github.com/BankrBot/skills/tree/main/aeon-token-pick',
    usageExample: '> get token pick with thesis\n> receive sizing guidance\n> set kill criterion',
    detailedDescription: 'Rigorously quantified token and prediction-market picks. Each recommendation includes a falsifiable investment thesis, position sizing guidance, and explicit kill criteria for risk management.',
  },
  {
    id: 'aeon-unlock-monitor',
    name: 'aeon-unlock-monitor',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Weekly token unlock tracker quantifying supply pressure via Absorption Ratio with one-line market reads per event.',
    downloads: 2600,
    stars: 130,
    version: '1.0.0',
    installCommand: '> install the aeon-unlock-monitor skill from https://github.com/BankrBot/skills/tree/main/aeon-unlock-monitor',
    usageExample: '> track weekly unlocks\n> check Absorption Ratio\n> get market read per event',
    detailedDescription: 'Weekly token unlock monitoring system that quantifies supply pressure using the Absorption Ratio metric. Delivers concise one-line market reads for each unlock event.',
  },
  {
    id: 'aeon-on-chain-monitor',
    name: 'aeon-on-chain-monitor',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Watchlist monitor over blockchain addresses and contracts: transfers, approvals, upgrades, gas spends, MEV interactions.',
    downloads: 2900,
    stars: 145,
    version: '1.0.0',
    installCommand: '> install the aeon-on-chain-monitor skill from https://github.com/BankrBot/skills/tree/main/aeon-on-chain-monitor',
    usageExample: '> monitor address transfers\n> track contract upgrades\n> detect MEV interactions',
    detailedDescription: 'Comprehensive on-chain watchlist monitoring for addresses and smart contracts. Tracks transfers, approvals, contract upgrades, gas spending patterns, and MEV interactions in real-time.',
  },
  {
    id: 'aeon-defi-monitor',
    name: 'aeon-defi-monitor',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'defi',
    description: 'Watchlist monitor over DeFi pools, lending markets, and vaults with APR/utilization/TVL/health-factor thresholds.',
    downloads: 3100,
    stars: 155,
    version: '1.0.0',
    installCommand: '> install the aeon-defi-monitor skill from https://github.com/BankrBot/skills/tree/main/aeon-defi-monitor',
    usageExample: '> monitor DeFi pool APR\n> track lending utilization\n> alert on health factor drops',
    detailedDescription: 'DeFi-specific watchlist monitoring for pools, lending markets, and vaults. Tracks APR fluctuations, utilization rates, TVL changes, and health factor thresholds with configurable alerts.',
  },
  {
    id: 'aeon-defi-overview',
    name: 'aeon-defi-overview',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'defi',
    description: 'Daily DeFi read with regime verdict (RISK-ON / NEUTRAL / RISK-OFF), sustainable-vs-incentive yield decomposition, fee leaders.',
    downloads: 3300,
    stars: 160,
    version: '1.0.0',
    installCommand: '> install the aeon-defi-overview skill from https://github.com/BankrBot/skills/tree/main/aeon-defi-overview',
    usageExample: '> get daily DeFi regime\n> decompose yield types\n> identify fee leaders',
    detailedDescription: 'Daily macro DeFi intelligence report. Delivers regime verdict (RISK-ON / NEUTRAL / RISK-OFF), decomposes sustainable yield from incentive-driven yield, and identifies top fee-generating protocols.',
  },
  {
    id: 'aeon-monitor-polymarket',
    name: 'aeon-monitor-polymarket',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Watchlist-driven Polymarket monitor surfacing only markets with material 24h moves, volume spikes, or fresh watched-account comments.',
    downloads: 2700,
    stars: 135,
    version: '1.0.0',
    installCommand: '> install the aeon-monitor-polymarket skill from https://github.com/BankrBot/skills/tree/main/aeon-monitor-polymarket',
    usageExample: '> track Polymarket moves\n> detect volume spikes\n> monitor watched accounts',
    detailedDescription: 'Intelligent Polymarket monitoring filtered by watchlists. Surfaces only markets with material 24-hour moves, significant volume spikes, or fresh comments from watched accounts to reduce noise.',
  },
  {
    id: 'aeon-monitor-kalshi',
    name: 'aeon-monitor-kalshi',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Watchlist-driven Kalshi monitor with cross-venue arb detection vs paired Polymarket markets (fee + slippage adjusted).',
    downloads: 2400,
    stars: 120,
    version: '1.0.0',
    installCommand: '> install the aeon-monitor-kalshi skill from https://github.com/BankrBot/skills/tree/main/aeon-monitor-kalshi',
    usageExample: '> track Kalshi markets\n> detect cross-venue arb\n> compare with Polymarket',
    detailedDescription: 'Kalshi prediction market monitoring with cross-venue arbitrage detection. Compares Kalshi markets against paired Polymarket markets with fee and slippage adjustments for actionable signals.',
  },
  {
    id: 'aeon-narrative-tracker',
    name: 'aeon-narrative-tracker',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Track rising, peaking, and fading narratives with mindshare + velocity and explicit positioning calls (FRONT-RUN / RIDE / FADE / WATCH / IGNORE).',
    downloads: 3600,
    stars: 175,
    version: '1.0.0',
    installCommand: '> install the aeon-narrative-tracker skill from https://github.com/BankrBot/skills/tree/main/aeon-narrative-tracker',
    usageExample: '> track rising narratives\n> get positioning call\n> measure mindshare velocity',
    detailedDescription: 'Narrative intelligence tracker that monitors rising, peaking, and fading market narratives using mindshare and velocity metrics. Delivers explicit positioning calls: FRONT-RUN, RIDE, FADE, WATCH, or IGNORE.',
  },
  {
    id: 'aeon-last30',
    name: 'aeon-last30',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Cross-platform social research over the last 30 days across Reddit, X/Twitter, Hacker News, Polymarket, and the open web.',
    downloads: 3900,
    stars: 185,
    version: '1.0.0',
    installCommand: '> install the aeon-last30 skill from https://github.com/BankrBot/skills/tree/main/aeon-last30',
    usageExample: '> research last 30 days on Reddit\n> aggregate Twitter sentiment\n> track HN trends',
    detailedDescription: 'Cross-platform social research aggregation covering the last 30 days. Pulls data from Reddit, X/Twitter, Hacker News, Polymarket, and the open web to build comprehensive sentiment and trend reports.',
  },
  {
    id: 'aeon-deep-research',
    name: 'aeon-deep-research',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'Exhaustive multi-source synthesis with explicit source credibility tiers, per-finding confidence, mandatory adversarial section.',
    downloads: 4200,
    stars: 200,
    version: '1.0.0',
    installCommand: '> install the aeon-deep-research skill from https://github.com/BankrBot/skills/tree/main/aeon-deep-research',
    usageExample: '> run deep multi-source research\n> assess source credibility\n> review adversarial section',
    detailedDescription: 'Deep research engine performing exhaustive multi-source synthesis. Every output includes explicit source credibility tiers, per-finding confidence scores, and a mandatory adversarial section challenging its own conclusions.',
  },
  {
    id: 'aeon-reg-monitor',
    name: 'aeon-reg-monitor',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Track legislation and regulatory actions affecting prediction markets, crypto, and AI agents — triaged by Stage × Impact.',
    downloads: 2300,
    stars: 115,
    version: '1.0.0',
    installCommand: '> install the aeon-reg-monitor skill from https://github.com/BankrBot/skills/tree/main/aeon-reg-monitor',
    usageExample: '> track crypto legislation\n> assess regulatory impact\n> monitor AI agent rules',
    detailedDescription: 'Regulatory intelligence tracker for prediction markets, crypto, and AI agents. Legislation and regulatory actions are triaged by Stage × Impact matrix to surface only the most consequential developments.',
  },
  {
    id: 'aeon-deal-flow',
    name: 'aeon-deal-flow',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Weekly funding round tracker across configurable verticals — primary-source required, filters re-announcements and rumors.',
    downloads: 2500,
    stars: 125,
    version: '1.0.0',
    installCommand: '> install the aeon-deal-flow skill from https://github.com/BankrBot/skills/tree/main/aeon-deal-flow',
    usageExample: '> track weekly funding rounds\n> filter re-announcements\n> focus on primary sources',
    detailedDescription: 'Weekly venture funding round tracker with configurable vertical focus. Strictly requires primary sources, automatically filters re-announcements and unverified rumors to deliver clean deal flow intelligence.',
  },
  {
    id: 'aeon-paper-pick',
    name: 'aeon-paper-pick',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'One AI/ML paper to read today from Hugging Face Papers — picks for novelty + falsifiability + reproducibility.',
    downloads: 2800,
    stars: 140,
    version: '1.0.0',
    installCommand: '> install the aeon-paper-pick skill from https://github.com/BankrBot/skills/tree/main/aeon-paper-pick',
    usageExample: '> get daily paper pick\n> check novelty score\n> verify reproducibility',
    detailedDescription: 'Curated daily AI/ML paper recommendations from Hugging Face Papers. Each pick is scored on novelty, falsifiability, and reproducibility to surface only the most impactful research.',
  },
  {
    id: 'aeon-huggingface-trending',
    name: 'aeon-huggingface-trending',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'Trending HF models, datasets, and spaces, filtered by license sanity and dedup with a "why notable" line per pick.',
    downloads: 3000,
    stars: 150,
    version: '1.0.0',
    installCommand: '> install the aeon-huggingface-trending skill from https://github.com/BankrBot/skills/tree/main/aeon-huggingface-trending',
    usageExample: '> find trending HF models\n> check license sanity\n> get "why notable" summary',
    detailedDescription: 'Trending intelligence from Hugging Face covering models, datasets, and spaces. Filters for license sanity, deduplicates similar entries, and provides a concise "why notable" rationale for each pick.',
  },
  {
    id: 'aeon-hacker-news-digest',
    name: 'aeon-hacker-news-digest',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Top HN stories filtered by configurable interests, with comment-mined insights and themed clustering.',
    downloads: 3400,
    stars: 165,
    version: '1.0.0',
    installCommand: '> install the aeon-hacker-news-digest skill from https://github.com/BankrBot/skills/tree/main/aeon-hacker-news-digest',
    usageExample: '> get top HN stories\n> filter by interests\n> mine comment insights',
    detailedDescription: 'Intelligent Hacker News digest filtered by configurable interests. Mines comment sections for insights and clusters stories by theme to surface the most relevant tech discussions.',
  },
  {
    id: 'aeon-rss-digest',
    name: 'aeon-rss-digest',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'tools',
    description: 'Daily roll-up across configurable RSS/Atom/JSON feeds with canonical-URL dedup, themed clustering, weighted ranking.',
    downloads: 2200,
    stars: 110,
    version: '1.0.0',
    installCommand: '> install the aeon-rss-digest skill from https://github.com/BankrBot/skills/tree/main/aeon-rss-digest',
    usageExample: '> configure RSS feeds\n> get daily roll-up\n> cluster by theme',
    detailedDescription: 'Configurable RSS/Atom/JSON feed aggregator. Features canonical-URL deduplication, themed clustering, and weighted ranking to deliver a clean daily intelligence roll-up from any source.',
  },
  {
    id: 'aeon-vuln-scanner',
    name: 'aeon-vuln-scanner',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'Audit trending repos for exploitable vulnerabilities with Semgrep + TruffleHog + osv-scanner + Slither and responsible-disclosure flow.',
    downloads: 1900,
    stars: 95,
    version: '1.0.0',
    installCommand: '> install the aeon-vuln-scanner skill from https://github.com/BankrBot/skills/tree/main/aeon-vuln-scanner',
    usageExample: '> audit repo for vulns\n> run Semgrep scan\n> trigger responsible disclosure',
    detailedDescription: 'Security audit engine for trending repositories. Combines Semgrep, TruffleHog, osv-scanner, and Slither to detect exploitable vulnerabilities with built-in responsible disclosure workflow.',
  },
  {
    id: 'aeon-skill-security-scan',
    name: 'aeon-skill-security-scan',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'Audit installed Bankr skills for shell injection, secret exfiltration, traversal, prompt-override payloads, obfuscation.',
    downloads: 1700,
    stars: 85,
    version: '1.0.0',
    installCommand: '> install the aeon-skill-security-scan skill from https://github.com/BankrBot/skills/tree/main/aeon-skill-security-scan',
    usageExample: '> scan skill for injection\n> detect secret exfiltration\n> check for obfuscation',
    detailedDescription: 'Security scanner specifically designed for installed Bankr skills. Detects shell injection, secret exfiltration, path traversal, prompt-override payloads, and code obfuscation attempts.',
  },
  {
    id: 'aeon-skill-evals',
    name: 'aeon-skill-evals',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'Validate any installed skill\'s output against an assertion manifest. Bootstrap mode generates a starter manifest from recent runs.',
    downloads: 1600,
    stars: 80,
    version: '1.0.0',
    installCommand: '> install the aeon-skill-evals skill from https://github.com/BankrBot/skills/tree/main/aeon-skill-evals',
    usageExample: '> validate skill output\n> generate assertion manifest\n> bootstrap from recent runs',
    detailedDescription: 'Output validation framework for installed skills. Validates any skill\'s output against a structured assertion manifest. Bootstrap mode auto-generates a starter manifest from recent historical runs.',
  },
  {
    id: 'aeon-skill-repair',
    name: 'aeon-skill-repair',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'Auto-diagnose and fix a failing or degraded installed skill; matched-playbook fixes with verification block.',
    downloads: 1550,
    stars: 75,
    version: '1.0.0',
    installCommand: '> install the aeon-skill-repair skill from https://github.com/BankrBot/skills/tree/main/aeon-skill-repair',
    usageExample: '> diagnose failing skill\n> apply playbook fix\n> verify repair success',
    detailedDescription: 'Self-healing system for installed skills. Auto-diagnoses failures or degradation, applies matched-playbook fixes, and verifies resolution through a built-in verification block.',
  },
  {
    id: 'aeon-autoresearch',
    name: 'aeon-autoresearch',
    author: 'Aeon',
    authorHandle: '@aeon',
    tag: 'Skill',
    category: 'other',
    description: 'Evolve any installed skill by generating four improved variations along separate theses, scoring on a weighted rubric, applying the winner.',
    downloads: 1800,
    stars: 90,
    version: '1.0.0',
    installCommand: '> install the aeon-autoresearch skill from https://github.com/BankrBot/skills/tree/main/aeon-autoresearch',
    usageExample: '> generate skill variations\n> score on weighted rubric\n> auto-apply best variant',
    detailedDescription: 'Meta-skill that evolves any installed skill by generating four improved variations along separate theses. Each variant is scored on a weighted rubric and the winner is automatically applied.',
  },
];